import { orvalClient } from "@api/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type { AIAnalysis, ListAnalysesResponse, RunAnalysisResponse } from "./types";

const QUERY_KEY = "ai-analyses";

export const useRunAnalysis = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { region_id: number; object_id: number }) => {
      const { data } = await orvalClient.post<RunAnalysisResponse>("/api/ai-analyses", params);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useAnalysis = (id?: string) =>
  useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: async () => {
      const { data } = await orvalClient.get<AIAnalysis>(`/api/ai-analyses/${id}`);
      return data;
    },
    enabled: !!id,
  });

export const useLatestAnalysis = (regionId?: number, objectId?: number) =>
  useQuery({
    queryKey: [QUERY_KEY, "latest", regionId, objectId],
    queryFn: async () => {
      try {
        const { data } = await orvalClient.get<AIAnalysis>("/api/ai-analyses/latest", {
          params: { region_id: regionId, object_id: objectId },
        });
        if (!data) return null;
        return data;
      } catch (err) {
        if ((err as AxiosError).response?.status === 404) return null;
        throw err;
      }
    },
    enabled: regionId !== undefined && objectId !== undefined,
  });

export const useAnalysisList = (regionId?: number, objectId?: number, page = 1, pageSize = 10) =>
  useQuery({
    queryKey: [QUERY_KEY, "list", regionId, objectId, page, pageSize],
    queryFn: async () => {
      const { data } = await orvalClient.get<ListAnalysesResponse>("/api/ai-analyses", {
        params: {
          region_id: regionId,
          object_id: objectId,
          page,
          page_size: pageSize,
        },
      });
      return data;
    },
    enabled: regionId !== undefined && objectId !== undefined,
  });

export const useSeedAnalysis = () => {
  return useMutation({
    mutationFn: async () => {
      const { data } = await orvalClient.post<{
        total_objects: number;
        skipped: number;
      }>("/api/admin/ai-analyses/seed");
      return data;
    },
  });
};
